import {
  Button, Flex, Text
} from '@huspy/briks-web';
import BankIcon from '@components/v2/BankIcon';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { addToAmplitudeEventContext, trackAmplitudeEvent } from '@shared/analytics/amplitude';
import useCredits from '@modules/core/hooks/useCredits';
import useGetCreditsPriceList from '@modules/core/hooks/queries/useGetCreditsPriceList';
import { USER_EVENTS } from '@shared/analytics/events';
import { BrokerageSubmissionSlido } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/BrokerageSubmissionSlido';
import { getHuspyActionNameBySubmissionsNumber } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/utils';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { AMPLITUDE_FEATURE_FLAGS } from '@modules/core/api/types';
import { useVaultReview } from './VaultReviewSlido/useVaultReview';
import { VaultReviewSlido } from './VaultReviewSlido';

const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.banksCard';

export const AllBanksCard = ({ opportunity }: { opportunity: Opportunity }) => {
  const [opened, { close, open }] = useDisclosure(false);
  const { t } = useTranslation();
  const { featureFlags, isFeatureFlagsLoading } = useFeatureFlags();
  const {
    isOpened: isVaultReviewOpened,
    handleVaultReview,
    close: closeVaultReviewSlido,
    isSubmitVaultLoading,
    vaultError,
  } = useVaultReview(
    opportunity.opportunity_external_id,
    opportunity.client_external_id!
  );

  const isCreditsModuleAvailableByFF = !isFeatureFlagsLoading
    && featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREDITS_MODULE];

  const { callCreditsAction } = useCredits();
  const { getActionPrice } = useGetCreditsPriceList(isCreditsModuleAvailableByFF);

  useEffect(() => {
    addToAmplitudeEventContext({
      client_id: opportunity.client_external_id,
      case_id: opportunity.opportunity_external_id,
    });
  }, [opportunity]);

  const hasOneSubmitted = opportunity.bank_applications.some(
    (item) => item.status !== BANK_APPLICATION_STATUS.draft
  );

  const getHuspyAllCreditsCost = (numberOfSubmissions: number) => new Array(numberOfSubmissions)
    .fill(null)
    .reduce((acc, _, i) => acc + getActionPrice(getHuspyActionNameBySubmissionsNumber(i)), 0);

  const onSubmitAllThroughHuspyClick = async () => {
    const shouldOpenVaultReview = await handleVaultReview();
    if (shouldOpenVaultReview) {
      return;
    }
    callCreditsAction(getHuspyAllCreditsCost(opportunity.bank_applications.length), () => {
      open();
    }, (error: Error) => {
      trackAmplitudeEvent(
        USER_EVENTS.CASE.CASE_SUBMISSION_FAILED,
        {
          useContext: true,
          blocked: 1,
          error_message: error.message,
          application_rank: 0,
          destination_type: 'Huspy',
          destinations: ['Huspy'],
          opportunity_id: opportunity.opportunity_external_id,
        }
      );
    });
  };

  return (
    <Flex direction='column' gap={ 2 } data-testid='all-banks-card'>
      <Flex
        backgroundColor='white'
        borderRadius='4'
        padding='6'
        align='center'
        justify='space-between'
        borderWidth='1px'
      >
        <Flex align='center'>
          <Flex>
            {opportunity.bank_applications.map(
              ({ bank_details, bank_application_external_id }, i) => (
                <BankIcon
                  key={ bank_application_external_id }
                  icon={ bank_details.icon }
                  height='48px'
                  width='48px'
                  pos='relative'
                  style={ { ...(i !== 0 && { left: `-${i * 15}px` }) } }
                />
              )
            )}
          </Flex>
          <Text>{t(`${baseTranslation}.allBanks`)}</Text>
        </Flex>
        <Flex gap='2' w='70%' justify='end'>
          <Button
            type='button'
            w='fit-content'
            size='2xl'
            background={ !hasOneSubmitted
              ? 'linear-gradient(103deg, #190E2D -5.71%, #6D40B6 110.46%);'
              : 'unset' }
            onClick={ onSubmitAllThroughHuspyClick }
            disabled={ hasOneSubmitted }
            data-testid='send-all-through-huspy-btn'
            loading={ isSubmitVaultLoading }
          >
            <Flex direction='column'>
              <Text data-test='send-all-through-huspy-btn' size='md'>{t(`${baseTranslation}.sendAllBanks`)}</Text>
              <Text size='sm'>{t(`${baseTranslation}.recommend`)}</Text>
            </Flex>
          </Button>
        </Flex>
      </Flex>
      { opened
        && (
          <BrokerageSubmissionSlido
            opened={ opened }
            close={ close }
            bankApplications={ opportunity.bank_applications }
            opportunityId={ opportunity.opportunity_external_id }
            clientExternalId={ opportunity.client_external_id! }
            oppportunity={ opportunity }
            isSubmitAll
          />
        )}
      {
        isVaultReviewOpened
        && (
          <VaultReviewSlido
            opened
            close={ closeVaultReviewSlido }
            opportunityId={ opportunity.opportunity_external_id }
            vaultError={ vaultError }
          />
        )
      }
    </Flex>
  );
};
